import { useEffect, type RefObject } from "react";
import * as THREE from "three";

import type { LoomBooleanConstructionTrace } from "../../api/loomRebuildConstructionTrace";
import { disposeLoomViewerObject } from "./loomGpuImplicitUnionViewer";
import { buildLoomViewerCandidate, commitLoomViewerCandidate } from "./loomViewerAtomicScene";
import {
  addLoomBooleanConstructionTracePresentation,
  loomBooleanConstructionTraceAnimator,
} from "./loomBooleanConstructionTraceViewer";

interface LoomBooleanConstructionTraceRuntime {
  advanceConstructionTrace?: (now: number) => boolean;
  constructionGroup: THREE.Group;
  group: THREE.Group;
  requestRender?: () => void;
}

function token(name: string, fallback: string): string {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim() || fallback;
}

export function useLoomBooleanConstructionTraceScene(
  runtimeRef: RefObject<LoomBooleanConstructionTraceRuntime | null>,
  trace: LoomBooleanConstructionTrace | null,
  showOnAcceptedAssembly: boolean,
): void {
  useEffect(() => {
    const runtime = runtimeRef.current;
    if (!runtime) return;
    const candidate = buildLoomViewerCandidate((candidateGroup) => (
      addLoomBooleanConstructionTracePresentation(
        candidateGroup,
        showOnAcceptedAssembly ? trace : null,
        new THREE.Box3().setFromObject(runtime.group),
        { prior: token("--fc-ink-5", "#75808c"), active: token("--fc-warning", "#d79a2b"),
          corrected: token("--fc-success", "#1b8b7a") },
      )
    ), disposeLoomViewerObject);
    commitLoomViewerCandidate(runtime.constructionGroup, candidate.group, disposeLoomViewerObject);
    runtime.advanceConstructionTrace = loomBooleanConstructionTraceAnimator(
      candidate.value, performance.now(),
    );
    runtime.requestRender?.();
  }, [runtimeRef, showOnAcceptedAssembly, trace]);
}
